import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { uploadFile, type ConflictPolicy, type UploadHandle } from './api'
import { joinPath } from './format'
import type { PickedFile } from './picked-files'

/**
 * 화면을 옮겨 다녀도 남는 "진행 중인 일" 하나.
 *
 * 지금 여기 들어오는 것은 업로드뿐이지만, 오래 걸리는 일이 업로드만은 아니다 -- 노드
 * 사이 전송도 같은 자리에 보여야 한다. 그래서 이름을 업로드에 묶어두지 않는다.
 */

/** One file plus what to do if something is already sitting at its destination. */
export interface PendingUpload extends PickedFile {
  onConflict: ConflictPolicy
}

/** Two at a time: enough to keep the link busy without starving any one file. */
const MAX_CONCURRENT = 2

export type ActivityStatus = 'queued' | 'running' | 'done' | 'error' | 'cancelled'

export interface ActivityItem {
  id: string
  name: string
  rootId: string
  targetPath: string
  loaded: number
  /** 0 이면 끝을 모르는 일이라는 뜻이다 — 막대 대신 도는 표시를 쓴다. */
  total: number
  /** 그 밑에 한 줄. 지금 무엇을 하고 있는지. */
  detail?: string
  status: ActivityStatus
  error?: string
  /** 멈출 수 있는 일인지. 이미 서버에서 도는 것은 여기서 멈출 수 없다. */
  cancellable: boolean
}

/** 우리가 굴리지 않는 일을 같은 자리에 얹었을 때, 그것을 움직이는 손잡이. */
export interface TrackedActivity {
  progress: (loaded: number, detail: string) => void
  finish: (error?: string) => void
}

interface ActivityState {
  items: ActivityItem[]
  enqueue: (pending: PendingUpload[], rootId: string, directory: string) => void
  cancel: (id: string) => void
  clearFinished: () => void
  /**
   * 여기서 굴리지 않는 일 하나를 같은 자리에 얹는다. 노드 사이 전송처럼 서버가 하는
   * 일도 사람에게는 똑같이 "진행 중인 일" 이다.
   */
  track: (
    name: string,
    rootId: string,
    targetPath: string,
    /** 있으면 그 일에 취소 버튼이 붙는다. 멈추는 방법을 아는 쪽은 부른 쪽뿐이다. */
    onCancel?: () => void,
  ) => TrackedActivity
  /** Fires whenever an upload lands, so the browser can refresh its listing. */
  onCompleted: (listener: (rootId: string, directory: string) => void) => () => void
}

const ActivityContext = createContext<ActivityState | null>(null)

export function ActivityProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ActivityItem[]>([])

  const queue = useRef<{ item: ActivityItem; file: File; onConflict: ConflictPolicy }[]>([])
  const active = useRef(new Map<string, UploadHandle>())
  /** 우리가 굴리지 않는 일을 멈추는 법. 서버로 한 마디 보내는 것이 보통이다. */
  const stoppers = useRef(new Map<string, () => void>())
  const listeners = useRef(new Set<(rootId: string, directory: string) => void>())

  const patch = useCallback((id: string, changes: Partial<ActivityItem>) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...changes } : item)),
    )
  }, [])

  const pump = useCallback(() => {
    while (active.current.size < MAX_CONCURRENT && queue.current.length > 0) {
      const next = queue.current.shift()
      if (!next) break
      const { item, file, onConflict } = next

      patch(item.id, { status: 'running' })

      const handle = uploadFile({
        rootId: item.rootId,
        path: item.targetPath,
        file,
        onConflict,
        onProgress: (loaded, total) => patch(item.id, { loaded, total }),
      })
      active.current.set(item.id, handle)

      handle.promise
        .then(() => {
          patch(item.id, { status: 'done', loaded: item.total })
          const directory = item.targetPath.slice(0, item.targetPath.lastIndexOf('/')) || '/'
          for (const listener of listeners.current) listener(item.rootId, directory)
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err)
          patch(item.id, {
            status: message.includes('cancelled') ? 'cancelled' : 'error',
            error: message,
          })
        })
        .finally(() => {
          active.current.delete(item.id)
          pump()
        })
    }
  }, [patch])

  const enqueue = useCallback(
    (pending: PendingUpload[], rootId: string, directory: string) => {
      const created = pending.map(({ file, path, onConflict }) => {
        const item: ActivityItem = {
          id: crypto.randomUUID(),
          name: path,
          rootId,
          targetPath: joinPath(directory, path),
          loaded: 0,
          total: file.size,
          status: 'queued',
          cancellable: true,
        }
        return { item, file, onConflict }
      })

      setItems((current) => [...current, ...created.map((entry) => entry.item)])
      queue.current.push(...created)
      pump()
    },
    [pump],
  )

  const track = useCallback(
    (
      name: string,
      rootId: string,
      targetPath: string,
      onCancel?: () => void,
    ): TrackedActivity => {
      const id = crypto.randomUUID()
      if (onCancel) stoppers.current.set(id, onCancel)
      setItems((current) => [
        ...current,
        {
          id,
          name,
          rootId,
          targetPath,
          loaded: 0,
          // 끝을 모른다. 미리 트리를 걸어 세면 옮기는 만큼 한 번 더 걷는 셈이다.
          total: 0,
          status: 'running',
          cancellable: onCancel !== undefined,
        },
      ])

      return {
        progress: (loaded, detail) => patch(id, { loaded, detail }),
        finish: (error) => {
          stoppers.current.delete(id)
          // 이미 "취소됨" 이라고 적힌 것을 "완료" 로 덮지 않는다. 멈춘 전송도 끝은
          // 끝이라 여기로 돌아오는데, 사람이 누른 것이 결과다.
          setItems((current) =>
            current.map((item) =>
              item.id === id && item.status === 'running'
                ? error === undefined
                  ? { ...item, status: 'done' }
                  : { ...item, status: 'error', error }
                : item,
            ),
          )
        },
      }
    },
    [patch],
  )

  const cancel = useCallback((id: string) => {
    active.current.get(id)?.abort()
    stoppers.current.get(id)?.()
    stoppers.current.delete(id)
    queue.current = queue.current.filter((entry) => entry.item.id !== id)
    setItems((current) =>
      current.map((item) =>
        item.id === id && (item.status === 'queued' || item.status === 'running')
          ? { ...item, status: 'cancelled' }
          : item,
      ),
    )
  }, [])

  const clearFinished = useCallback(() => {
    setItems((current) =>
      current.filter((item) => item.status === 'queued' || item.status === 'running'),
    )
  }, [])

  const onCompleted = useCallback((listener: (rootId: string, directory: string) => void) => {
    listeners.current.add(listener)
    return () => listeners.current.delete(listener)
  }, [])

  const value = useMemo<ActivityState>(
    () => ({ items, enqueue, cancel, clearFinished, onCompleted, track }),
    [items, enqueue, cancel, clearFinished, onCompleted, track],
  )

  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>
}

export function useActivity(): ActivityState {
  const context = useContext(ActivityContext)
  if (!context) throw new Error('useActivity must be used inside ActivityProvider')
  return context
}
